import { addDays, differenceInDays, isBefore } from 'date-fns';
import { ApprovalMethodNoticePeriodType } from '../approval-method-notice-period';
import { InvoiceStatus } from './model';
// Re-export summary-level selectors so existing callers like `invoiceModel.formattedNumber`
// keep working. All of these only read fields present on `invoiceSummary.T` and accept both
// summary and full invoices via structural subtyping.
export { accounting, customerName, displayStatus, dueDate, formattedNumber, id, isNotSynced, isPaymentMethodUnset, isPaymentMethodUnverified, isRefundFailed, isRefundRequested, isSynced, issuedOn, otherPartyCompanyName, paymentDetails, qboIntegration, rawTotalAmount, relationshipId, totalAmount, vendorName, xeroIntegration } from '../invoice-summary/selectors';
export function isPending(invoice) {
    return invoice.status.type === InvoiceStatus.Pending;
}
export function isPaid(invoice) {
    return invoice.status.type === InvoiceStatus.Paid;
}
export function isConfirmed(invoice) {
    return invoice.status.type === InvoiceStatus.PaymentConfirmed;
}
export function isProcessing(invoice) {
    return invoice.status.type === InvoiceStatus.ProcessingCharge;
}
export function isPaidExternally(invoice) {
    return invoice.status.type === InvoiceStatus.PaidExternally;
}
export function isVoided(invoice) {
    return invoice.status.type === InvoiceStatus.Voided;
}
export function isRefunded(invoice) {
    return invoice.status.type === InvoiceStatus.Refunded;
}
export function isPartiallyRefunded(invoice) {
    return invoice.status.type === InvoiceStatus.PartiallyRefunded;
}
export function totalRefunded(invoice) {
    var _invoice_payment;
    return ((_invoice_payment = invoice.payment) === null || _invoice_payment === void 0 ? void 0 : _invoice_payment.totalRefunded()) || '0';
}
export function hasRefundFees(invoice) {
    var _invoice_payment;
    return ((_invoice_payment = invoice.payment) === null || _invoice_payment === void 0 ? void 0 : _invoice_payment.hasRefundFees()) || false;
}
export function isPaymentStopped(invoice) {
    return invoice.status.type === InvoiceStatus.PaymentStopped;
}
export function isPaymentFailed(invoice) {
    return invoice.status.type === InvoiceStatus.PaymentFailed;
}
export function isRequiresPaymentApproval(invoice) {
    return invoice.status.type === InvoiceStatus.RequiresPaymentApproval;
}
export function isPaymentActionRequired(invoice) {
    return invoice.status.type === InvoiceStatus.PaymentActionRequired;
}
export function isDisputed(invoice) {
    return invoice.status.type === InvoiceStatus.PaymentDisputed;
}
export function statusType(invoice) {
    return invoice.status.type;
}
export function disputedMessage(invoice) {
    return invoice.status.type === InvoiceStatus.PaymentDisputed ? invoice.status.paymentDisputed.message : null;
}
export function issueDate(invoice) {
    return invoice.issuedOn || invoice.createdOn;
}
//in case user billed now with a past issue date, we should use the createdOn date
export function paymentProcessingDate(invoice) {
    return isBefore(invoice.rawDueDate, invoice.rawCreatedOn) ? invoice.createdOn : invoice.dueDate;
}
export function paymentFailureMessage(param) {
    let { status } = param;
    return status.type === InvoiceStatus.PaymentFailed ? status.paymentFailed.message : undefined;
}
export function paymentFailureCode(param) {
    let { status } = param;
    return status.type === InvoiceStatus.PaymentFailed ? status.paymentFailed.failureCode : undefined;
}
export function processingAction(param) {
    let { status } = param;
    if (status.type === InvoiceStatus.PaymentActionRequired) {
        const { processingAction } = status.paymentActionRequired;
        return {
            stripeUrl: processingAction.url,
            clientSecret: processingAction.clientSecret
        };
    } else {
        return {
            stripeUrl: null,
            clientSecret: null
        };
    }
}
export function isStableIntegrationStatus(invoice) {
    const trueFnc = ()=>true;
    return invoice.integration.accept({
        whenPendingSync: trueFnc,
        whenInvoiceSyncFailed: trueFnc,
        whenPaymentSyncFailed: trueFnc,
        whenRefundSyncFailed: trueFnc,
        whenVoidedRecorded: trueFnc,
        whenPaymentRecorded: trueFnc,
        whenRefundRecorded: trueFnc,
        whenInvoiceRecorded: ()=>isPending(invoice) || isPaidExternally(invoice) || isPaymentStopped(invoice) || isPaid(invoice) && invoice.isTotalAmountZero || isRequiresPaymentApproval(invoice) || isPaymentActionRequired(invoice)
    });
}
export function paymentStopReason(invoice) {
    return invoice.status.type === InvoiceStatus.PaymentStopped ? invoice.status.paymentStopped.reason : '';
}
export const autoApprovalDate = (invoice)=>{
    const { status, createdOn } = invoice;
    const approvalMethod = status.type === InvoiceStatus.RequiresPaymentApproval ? status.requiresPaymentApproval.approvalMethod : null;
    return approvalMethod && approvalMethod.type === ApprovalMethodNoticePeriodType.Automatic ? addDays(new Date(createdOn), approvalMethod.auto.noticePeriodDays) : null;
};
export const itemName = (param)=>{
    let { name } = param;
    return name;
};
export const itemQuantity = (param)=>{
    let { quantity } = param;
    return quantity || '';
};
export const itemRate = (param)=>{
    let { rate } = param;
    return rate;
};
export const itemDiscount = (param)=>{
    let { discount } = param;
    return discount;
};
export const itemTotalAmount = (param)=>{
    let { totalAmount } = param;
    return totalAmount;
};
export const subItemName = (param)=>{
    let { name } = param;
    return name;
};
export const subItemRate = (param)=>{
    let { rate } = param;
    return rate;
};
export const subItemDiscount = (param)=>{
    let { discount } = param;
    return discount;
};
export const subItemTotalAmount = (param)=>{
    let { totalAmount } = param;
    return totalAmount;
};
export const subItemUnitName = (param)=>{
    let { unitName } = param;
    return unitName;
};
export const groupedLineItems = (item)=>item.groupedLineItems;
export const isGrouped = (item)=>groupedLineItems(item).length > 0;
export const payment = (param)=>{
    let { payment } = param;
    return payment;
};
export const numRefunds = (invoice)=>{
    var _ref;
    var _invoice_payment_refunds, _invoice_payment;
    return (_ref = (_invoice_payment = invoice.payment) === null || _invoice_payment === void 0 ? void 0 : (_invoice_payment_refunds = _invoice_payment.refunds) === null || _invoice_payment_refunds === void 0 ? void 0 : _invoice_payment_refunds.length) !== null && _ref !== void 0 ? _ref : null;
};
export const daysSincePayment = (invoice)=>{
    if (invoice.status.type === InvoiceStatus.Paid) {
        const paidTimestamp = invoice.status.paid.paymentDate;
        return differenceInDays(new Date().setHours(0, 0, 0, 0), new Date(paidTimestamp).setHours(0, 0, 0, 0));
    } else {
        return null;
    }
};
